import { cellAtWorld, cellId, findPath, hexDist, hexToWorld, heroWalkable, isInside, isWall, worldToHex, type BattleState, type Hex } from '@somnhold/sim';

/**
 * Inside the walls, districts included (the wall line itself counts: a gate or a breach). Terraced land has no
 * ring to help through: its walled ground is only worked out for a wave (`land.inside`), so in the bastion it
 * holds the keep alone and helping never counted there (2026-11-01). Anywhere goes on land.
 */
export const insideWall = (town: BattleState, x: number, y: number): boolean => {
  if (town.map.land) return true;
  const h = worldToHex(x, y);
  return isInside(town.map, h.q, h.r) || isWall(town.map, h.q, h.r);
};

/** How many steps out from a site a free cell to help from is looked for. */
const SPOT_RINGS = 4;

/**
 * Where to stand to help at a site: the free cell next to it nearest to the hero, on the inside of
 * the wall, so a site against the ring never sends the hero round through a gate to work from outside.
 * A site boxed in by towers and walls is helped from farther in (up to four steps), from a cell the
 * hero can actually walk to.
 */
export const helpSpot = (town: BattleState, site: Hex, from: { x: number; y: number } | undefined): { x: number; y: number } => {
  const R = town.map.radius;
  const fromCell = from ? cellAtWorld(town, from.x, from.y) : -1;
  for (let ring = 1; ring <= SPOT_RINGS; ring++) {
    const spots: { id: number; p: { x: number; y: number }; d: number }[] = [];
    for (let q = site.q - ring; q <= site.q + ring; q++) {
      for (let r = site.r - ring; r <= site.r + ring; r++) {
        if (hexDist({ q, r }, site) !== ring) continue;
        const id = cellId(R, q, r);
        if (id < 0 || (!town.map.land && !isInside(town.map, q, r)) || !heroWalkable(town, id)) continue;
        const p = hexToWorld(q, r);
        spots.push({ id, p, d: from ? Math.hypot(p.x - from.x, p.y - from.y) : 0 });
      }
    }
    spots.sort((a, b) => a.d - b.d || a.id - b.id);
    for (const s of spots) {
      if (fromCell < 0 || s.id === fromCell) return s.p;
      const path = findPath(town, fromCell, s.id);
      if (path[path.length - 1] === s.id) return s.p;
    }
  }
  return hexToWorld(site.q, site.r);
};
